import React from "react";
import { Link } from "react-router-dom";
import { clean, count, money, percent, prettyDate, share } from "../format";
import { PRESETS } from "../periods";
import { Empty, Skeleton } from "./primitives";

/* --------------------------------------------------------------- split meter */

/**
 * Two-part bar: `parts[0]` is the headline share.
 * parts: [{ label, value, color, note? }, { ... }]
 */
export const SplitMeter = ({ parts, children }) => {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  const [lead] = parts;
  const leadShare = percent(share(lead.value, total));

  return (
    <div className="dash-meter">
      <div className="dash-meter-head">
        <span>{lead.label}</span>
        <strong>{leadShare}</strong>
      </div>
      <div
        className="dash-meter-track"
        role="meter"
        aria-label={`${lead.label} share`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share(lead.value, total))}
        aria-valuetext={leadShare}
      >
        {parts.map((part) => (
          <div key={part.label} className="dash-meter-fill" style={{ width: `${share(part.value, total)}%`, background: part.color }} />
        ))}
      </div>
      <div className="dash-meter-legend">
        {parts.map((part) => (
          <span key={part.label}>
            <i className="dash-swatch" style={{ background: part.color }} aria-hidden="true" />
            {part.label} {money(part.value)}
            {part.note && <span className="dash-muted"> · {part.note}</span>}
          </span>
        ))}
      </div>
      {children}
    </div>
  );
};

export const MiniList = ({ title, rows, format = money }) => (
  <div className="dash-meter">
    <div className="dash-meter-head">
      <span>{title}</span>
    </div>
    <ul className="dash-minilist">
      {rows.map((row) => (
        <li key={row.name}>
          <span>{row.name}</span>
          <strong>{format(row.value)}</strong>
        </li>
      ))}
    </ul>
  </div>
);

/* ---------------------------------------------------------------------- tables */

export const TableState = ({ loading, empty, children }) => {
  if (loading) {
    return (
      <div className="pd-20">
        <Skeleton height={180} />
      </div>
    );
  }
  if (empty) return <Empty />;
  return <div className="dash-table-wrap">{children}</div>;
};

export const StatusPill = ({ good, tone, children }) => (
  <span className={`dash-pill dash-pill--${tone || (good ? "good" : "warn")}`}>{children}</span>
);

export const DistributorsTable = ({ rows, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Top distributors by billed value</caption>
      <thead>
        <tr>
          <th scope="col">Distributor</th>
          <th scope="col" className="num">
            Invoices
          </th>
          <th scope="col" className="num">
            Billed
          </th>
          <th scope="col" className="num">
            Due
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.id}>
            <td>
              <span className="dash-rank" aria-hidden="true">
                {index + 1}
              </span>
              <Link to={`/distributor-details/${row.id}`}>{clean(row.name)}</Link>
            </td>
            <td className="num">{count(row.orders)}</td>
            <td className="num">{money(row.value)}</td>
            <td className="num">{row.due > 0 ? <StatusPill>{money(row.due)}</StatusPill> : <StatusPill good>Clear</StatusPill>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

export const RecentOrdersTable = ({ rows, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Latest primary sales invoices</caption>
      <thead>
        <tr>
          <th scope="col">Invoice</th>
          <th scope="col">Distributor</th>
          <th scope="col" className="num">
            Value
          </th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const delivered = row.delivery_status === "delivered";
          return (
            <tr key={row.id}>
              <td>
                <Link to={`/primary-sales-detail/${row.id}`}>{row.receipt_no}</Link>
                <div className="dash-muted tx-11">{prettyDate(row.date)}</div>
              </td>
              <td>{clean(row.distributor_name)}</td>
              <td className="num">{money(row.total_price)}</td>
              <td>
                <StatusPill good={delivered}>{delivered ? "Delivered" : "Pending"}</StatusPill>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </TableState>
);

/**
 * Demand sheets that are still open and not fully invoiced - the supply chain
 * work queue. Ordered by the money left on the sheet, not by date.
 */
export const OpenDemandsTable = ({ rows, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Open demand sheets with value still to invoice</caption>
      <thead>
        <tr>
          <th scope="col">Demand</th>
          <th scope="col">Distributor</th>
          <th scope="col" className="num">
            Demanded
          </th>
          <th scope="col" className="num">
            Open
          </th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>
              <Link to={`/demand-sheet-receipt/${row.id}`}>{row.receipt_no}</Link>
              <div className="dash-muted tx-11">
                {prettyDate(row.date)} · {count(row.age_days)}d old
              </div>
            </td>
            <td>{clean(row.distributor_name)}</td>
            <td className="num">
              {money(row.demanded)}
              <div className="dash-muted tx-11">{count(row.orders)} order(s) · {money(row.invoiced)} billed</div>
            </td>
            <td className="num">{money(row.pending)}</td>
            <td>
              {row.status === "draft" ? (
                <StatusPill tone="mute">Awaiting approval</StatusPill>
              ) : (
                <StatusPill good={row.invoiced > 0}>{row.invoiced > 0 ? "Part invoiced" : "Not invoiced"}</StatusPill>
              )}
              {row.locked && <div className="dash-muted tx-11">Locked</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

/* ------------------------------------------------------------------ filter bar */

export const FilterBar = ({ preset, range, onPreset, onBound, presets = PRESETS }) => (
  <div className="dash-filters">
    <div className="dash-presets" role="group" aria-label="Reporting period">
      {presets.map((item) => (
        <button key={item.key} type="button" className="dash-preset" aria-pressed={preset === item.key} onClick={() => onPreset(item.key)}>
          {item.label}
        </button>
      ))}
    </div>

    <div className="dash-range" role="group" aria-label="Custom date range">
      <label htmlFor="dash-from">From</label>
      <input id="dash-from" type="date" value={range.from || ""} max={range.to} onChange={(e) => onBound("from", e.target.value)} />
      <label htmlFor="dash-to">To</label>
      <input id="dash-to" type="date" value={range.to} min={range.from || undefined} onChange={(e) => onBound("to", e.target.value)} />
    </div>
  </div>
);
